import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationSettings } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { notificationsApi } from '../../lib/notifications-api.js';

// NOT-3 (Opt-out): Nutzer entscheidet je Event-Typ, ob er dazu benachrichtigt werden möchte.
// Fehlt ein Feld im Server-Payload (noch keine Einstellung gespeichert) -> Default true
// (siehe NotificationSettingsSchema-Kommentar in packages/shared).
const SETTINGS_FIELDS = ['defect_assigned', 'retest_due', 'tester_digest'] as const;

export function NotificationSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notif-settings'],
    queryFn: notificationsApi.getSettings,
  });

  const patch = useMutation({
    mutationFn: (p: Partial<NotificationSettings>) => notificationsApi.patchSettings(p),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-settings'] });
    },
  });

  const labelFor: Record<(typeof SETTINGS_FIELDS)[number], string> = {
    defect_assigned: t('settings.notifDefectAssigned'),
    retest_due: t('settings.notifRetestDue'),
    tester_digest: t('settings.notifTesterDigest'),
  };

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-lg font-bold text-text-primary">{t('settings.title')}</h1>
      <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <ul className="space-y-3">
          {SETTINGS_FIELDS.map((field) => {
            const checked = data?.[field] ?? true;
            return (
              <li key={field}>
                <label className="flex items-center gap-3 text-sm text-text-body">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => patch.mutate({ [field]: e.target.checked })}
                  />
                  <span>{labelFor[field]}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
